import { useState, useEffect } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface Purchase {
  id: string;
  product_title: string;
  price: number;
  purchased_at: string;
  product_type: string;
}

interface MonthlyData {
  month: string;
  sales: number;
  count: number;
}

interface ProductSales {
  title: string;
  revenue: number;
  count: number;
  type: string;
}

const COLORS = ["#2D6A4F", "#C9A84C", "#40916C", "#D4A017", "#52B788", "#B7941D"];

function formatYen(val: number) {
  if (val >= 10000) return `¥${(val / 10000).toFixed(1)}万`;
  return `¥${val.toLocaleString()}`;
}

function CustomTooltipArea({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm font-sans">
      <p className="text-charcoal/60 text-xs mb-1">{label}</p>
      <p className="text-deep-green font-medium">¥{payload[0].value.toLocaleString()}</p>
    </div>
  );
}

export default function ProviderAnalytics() {
  const { currentUser } = useApp();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<"3m" | "6m" | "12m">("6m");

  useEffect(() => {
    if (!currentUser?.id) return;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("purchases")
        .select("id, product_title, price, purchased_at, product_type")
        .eq("provider_id", currentUser.id)
        .eq("status", "completed")
        .order("purchased_at", { ascending: false });
      setPurchases(data || []);
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  // --- Date range ---
  const months = period === "3m" ? 3 : period === "6m" ? 6 : 12;
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);

  const filteredPurchases = purchases.filter(
    (p) => new Date(p.purchased_at) >= startDate
  );

  // --- Monthly trend ---
  const monthlyMap: Record<string, { sales: number; count: number }> = {};
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthlyMap[key] = { sales: 0, count: 0 };
  }
  filteredPurchases.forEach((p) => {
    const d = new Date(p.purchased_at);
    const key = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (monthlyMap[key]) {
      monthlyMap[key].sales += p.price;
      monthlyMap[key].count += 1;
    }
  });
  const monthlyData: MonthlyData[] = Object.entries(monthlyMap).map(([month, v]) => ({
    month: month.replace(/^\d{4}\//, "") + "月",
    sales: v.sales,
    count: v.count,
  }));

  // --- Product breakdown ---
  const productMap: Record<string, { revenue: number; count: number; type: string }> = {};
  filteredPurchases.forEach((p) => {
    if (!productMap[p.product_title]) {
      productMap[p.product_title] = { revenue: 0, count: 0, type: p.product_type };
    }
    productMap[p.product_title].revenue += p.price;
    productMap[p.product_title].count += 1;
  });
  const productSales: ProductSales[] = Object.entries(productMap)
    .map(([title, v]) => ({ title, ...v }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 8);

  // --- Type breakdown ---
  const videoRev = filteredPurchases.filter((p) => p.product_type === "video").reduce((s, p) => s + p.price, 0);
  const kitRev = filteredPurchases.filter((p) => p.product_type === "kit").reduce((s, p) => s + p.price, 0);
  const typeData = [
    { name: "動画講座", value: videoRev },
    { name: "教材キット", value: kitRev },
  ].filter((d) => d.value > 0);

  // --- Summary stats ---
  const totalRevenue = filteredPurchases.reduce((s, p) => s + p.price, 0);
  const totalCount = filteredPurchases.length;
  const avgPrice = totalCount > 0 ? Math.round(totalRevenue / totalCount) : 0;
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevMonthRev = purchases
    .filter((p) => new Date(p.purchased_at) >= prevMonthStart && new Date(p.purchased_at) < prevMonthEnd)
    .reduce((s, p) => s + p.price, 0);
  const currentMonthRev = purchases
    .filter((p) => new Date(p.purchased_at) >= currentMonthStart)
    .reduce((s, p) => s + p.price, 0);
  const growth = prevMonthRev > 0 ? ((currentMonthRev - prevMonthRev) / prevMonthRev) * 100 : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + period selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="font-display italic text-gold text-xs tracking-widest mb-1">Sales Analytics</p>
          <h2 className="font-serif text-deep-green text-2xl font-light">売上・分析</h2>
        </div>
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
          {(["3m", "6m", "12m"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${period === p ? "bg-white text-deep-green font-medium" : "text-charcoal/50 hover:text-charcoal"}`}
            >
              {p === "3m" ? "直近3ヶ月" : p === "6m" ? "直近6ヶ月" : "直近12ヶ月"}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            label: "期間売上",
            value: `¥${totalRevenue.toLocaleString()}`,
            icon: "ri-money-yen-circle-line",
            color: "text-gold",
            sub: null,
          },
          {
            label: "今月売上",
            value: `¥${currentMonthRev.toLocaleString()}`,
            icon: "ri-bar-chart-2-line",
            color: "text-deep-green",
            sub: growth !== null ? (
              <span className={`text-xs font-sans flex items-center gap-0.5 ${growth >= 0 ? "text-teal" : "text-red-400"}`}>
                <i className={growth >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                {Math.abs(growth).toFixed(1)}% 先月比
              </span>
            ) : null,
          },
          {
            label: "販売件数",
            value: `${totalCount}件`,
            icon: "ri-shopping-bag-line",
            color: "text-teal",
            sub: null,
          },
          {
            label: "平均単価",
            value: avgPrice > 0 ? `¥${avgPrice.toLocaleString()}` : "—",
            icon: "ri-price-tag-3-line",
            color: "text-gold",
            sub: null,
          },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-100">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className={`${s.icon} ${s.color} text-2xl`} />
            </div>
            <p className="font-serif text-2xl text-charcoal mt-2">{s.value}</p>
            <p className="text-xs text-charcoal/50 font-sans mt-0.5">{s.label}</p>
            {s.sub && <div className="mt-1">{s.sub}</div>}
          </div>
        ))}
      </div>

      {filteredPurchases.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-100 py-20 text-center">
          <i className="ri-bar-chart-line text-5xl text-charcoal/15 block mb-4" />
          <p className="font-serif text-charcoal/40 text-lg">まだ売上データがありません</p>
          <p className="text-sm text-charcoal/30 font-sans mt-2">商品を出品して最初の販売を目指しましょう</p>
        </div>
      ) : (
        <>
          {/* Monthly Revenue Area Chart */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="font-serif text-deep-green text-lg mb-6">月別売上推移</h3>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2D6A4F" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#2D6A4F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f0ec" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#888", fontFamily: "Noto Sans JP" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatYen} tick={{ fontSize: 11, fill: "#888", fontFamily: "Noto Sans JP" }} axisLine={false} tickLine={false} width={60} />
                <Tooltip content={<CustomTooltipArea />} />
                <Area
                  type="monotone"
                  dataKey="sales"
                  stroke="#2D6A4F"
                  strokeWidth={2.5}
                  fill="url(#salesGrad)"
                  dot={{ fill: "#2D6A4F", strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5, fill: "#2D6A4F" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product breakdown bar */}
            {productSales.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <h3 className="font-serif text-deep-green text-lg mb-5">商品別売上ランキング</h3>
                <div className="space-y-3">
                  {productSales.map((p, i) => {
                    const maxRev = productSales[0]?.revenue || 1;
                    const pct = (p.revenue / maxRev) * 100;
                    return (
                      <div key={p.title}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs text-charcoal/30 font-sans font-medium w-5 flex-shrink-0">#{i + 1}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-sans flex-shrink-0 ${p.type === "video" ? "bg-teal/10 text-teal" : "bg-gold/10 text-gold"}`}>
                              {p.type === "video" ? "動画" : "キット"}
                            </span>
                            <span className="text-xs font-sans text-charcoal truncate">{p.title}</span>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="text-xs font-sans font-medium text-charcoal">¥{p.revenue.toLocaleString()}</p>
                            <p className="text-xs text-charcoal/40 font-sans">{p.count}件</p>
                          </div>
                        </div>
                        <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${pct}%`,
                              background: i === 0 ? "#2D6A4F" : i === 1 ? "#40916C" : "#C9A84C",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Type pie + bar */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5">
              <h3 className="font-serif text-deep-green text-lg">商品タイプ別売上比率</h3>
              {typeData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={typeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {typeData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        formatter={(value) => (
                          <span style={{ fontSize: "11px", fontFamily: "Noto Sans JP", color: "#666" }}>{value}</span>
                        )}
                      />
                      <Tooltip
                        formatter={(value: number) => [`¥${value.toLocaleString()}`, "売上"]}
                        contentStyle={{ fontFamily: "Noto Sans JP", fontSize: "12px", borderRadius: "10px", border: "1px solid #e7e5e4" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-3">
                    {typeData.map((d, i) => (
                      <div key={d.name} className="rounded-xl p-3" style={{ background: `${COLORS[i]}10` }}>
                        <p className="text-xs text-charcoal/50 font-sans">{d.name}</p>
                        <p className="font-serif text-lg mt-1" style={{ color: COLORS[i] }}>¥{d.value.toLocaleString()}</p>
                        <p className="text-xs text-charcoal/40 font-sans">
                          {totalRevenue > 0 ? ((d.value / totalRevenue) * 100).toFixed(1) : 0}%
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-charcoal/30 font-sans text-sm">データなし</div>
              )}
            </div>
          </div>

          {/* Monthly count bar chart */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="font-serif text-deep-green text-lg mb-6">月別販売件数</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f0ec" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#888", fontFamily: "Noto Sans JP" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#888", fontFamily: "Noto Sans JP" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  formatter={(value: number) => [`${value}件`, "販売件数"]}
                  contentStyle={{ fontFamily: "Noto Sans JP", fontSize: "12px", borderRadius: "10px", border: "1px solid #e7e5e4" }}
                />
                <Bar dataKey="count" fill="#C9A84C" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Recent transactions */}
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-serif text-deep-green text-lg">取引履歴</h3>
              <span className="text-xs text-charcoal/40 font-sans">直近{filteredPurchases.length}件</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    {["購入日", "商品名", "種別", "金額"].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-sans text-charcoal/50 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {filteredPurchases.slice(0, 20).map((p) => (
                    <tr key={p.id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-5 py-3 text-xs font-sans text-charcoal/60 whitespace-nowrap">
                        {new Date(p.purchased_at).toLocaleDateString("ja-JP")}
                      </td>
                      <td className="px-5 py-3 text-sm font-sans text-charcoal max-w-[200px] truncate">{p.product_title}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${p.product_type === "video" ? "bg-teal/10 text-teal" : "bg-gold/10 text-gold"}`}>
                          {p.product_type === "video" ? "動画" : "キット"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm font-sans text-charcoal font-medium whitespace-nowrap">
                        ¥{p.price.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
